import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { DataTable, HelpPopover, type IDataColumn } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IAdminTeamFirm, IAdminTeamFirmUnmapped } from '../api/team-firm-contracts';
import { teamFirmsApi } from '../api/team-firms-api';
import { LoadError, useAdminData } from '../components/admin-page';
import { NoteLine } from './person-hub/said';
import {
  CRD_DIGITS,
  OTHER_RIA,
  NamePicker,
  RouteChangeLine,
  attachName,
  isGone,
  crdOf,
  mayHaveLanded,
  refusalText,
  changeStamp,
  useWriteNote
} from './team-firm-form';

/** The Firms table's anchor for this block — where `/unmapped`, its old address, lands. */
export const NOT_MATCHED_ANCHOR = 'not-matched';

/**
 * "Not matched yet" over the Firms table (SR-513): what links no firm to a name yet, in two
 * parts, drawn only while either holds something (or there is something to say). **A fold, shut
 * unless the address asks for it** (`#not-matched`) or a write has something to say: its count is
 * the state, and the table under it stays on the first screen.
 *
 * - **Met at registration** — the firms a registration's BrokerCheck or IAPD lookup named whose
 *   CRD no register name holds (`GET /admin/team-firms/unmapped`, most registrations first).
 *   "Add as a firm" opens the page's Add a firm, prefilled with the CRD and the name the lookup
 *   gave; "Attach to a firm" adds the CRD to a register name.
 * - **Names with no CRD** — register names holding no CRD, from the register the page reads.
 *   "Attach to a firm" adds a typed CRD to the name, which then shows as that firm's other name.
 *
 * Both attaches are the register name's set **read afresh, just before**, plus the CRD
 * (`attachName`). The unmapped firms are read again whenever the register is (`register`, the
 * page's read handed down), so the page's *Refresh* and every write reach both. A write that lands
 * takes its row away, so it is said above the rows; a refusal beside the row it came from.
 */
export function UnmatchedFirms({
  register,
  reread,
  notPermitted,
  onAddAsFirm
}: {
  register: IAdminTeamFirm[];
  /** The page's table and register, read again. */
  reread: () => Promise<boolean>;
  /** The id of the page's `NotPermitted` sentence; present, every press is drawn off. */
  notPermitted?: string;
  onAddAsFirm: (seen: IAdminTeamFirmUnmapped) => void;
}) {
  const { data, error } = useAdminData<IAdminTeamFirmUnmapped[]>(
    (signal) => teamFirmsApi.unmapped(signal),
    [register]
  );
  const { note: done, setNote: setDone, landed, lost } = useWriteNote(reread, 'register');
  const seen = data ?? [];
  // Other/RIA holds no CRD by design: Rankings' bucket, not a firm to match.
  const noCrd = register.filter((name) => name.finraFirmIds.length === 0 && name.code !== OTHER_RIA);

  const rowWrites: IRowWrites = {
    register,
    notPermitted,
    begin: () => setDone(null),
    landed,
    lost,
    reread
  };
  const seenColumns = React.useMemo(
    () => metColumns({ ...rowWrites, onAddAsFirm }),
    [register, notPermitted, reread, onAddAsFirm]
  );
  const nameColumns = React.useMemo(() => noCrdColumns(rowWrites), [register, notPermitted, reread]);

  const shown = seen.length > 0 || noCrd.length > 0 || error !== null || done !== null;
  const { hash } = useLocation();
  const asked = hash === `#${NOT_MATCHED_ANCHOR}`;
  const [open, setOpen] = React.useState(asked);
  const section = React.useRef<HTMLElement>(null);
  const scrolled = React.useRef(false);
  React.useEffect(() => {
    if (!shown || data === null || scrolled.current || !asked) return;
    scrolled.current = true;
    setOpen(true);
    section.current?.scrollIntoView({ block: 'start' });
  }, [shown, data, asked]);
  const bodyId = React.useId();

  if (!shown) return null;
  return (
    <section
      ref={section}
      id={NOT_MATCHED_ANCHOR}
      aria-label="Not matched yet"
      data-unmatched=""
      className="grid scroll-mt-20 gap-3"
    >
      <h2 className="flex items-center gap-1 text-[0.9375rem] font-medium text-[var(--color-royal-blue)]">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          data-open={open ? '' : undefined}
          onClick={() => setOpen(!open)}
          className="-ml-1 flex items-center gap-1 rounded px-1 hover:bg-[var(--accent)]"
        >
          <ChevronRight
            className={`size-4 transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden
          />
          Not matched yet
          {data !== null && ` (${(seen.length + noCrd.length).toLocaleString('en-US')})`}
        </button>
        <HelpPopover label="About not matched yet">
          <p>
            Met at registration: firms a registration’s BrokerCheck or IAPD lookup named that no
            name on the register holds. Add one as a firm, or attach its CRD to a name the register
            holds, and the next registration there is named by it.
          </p>
          <p>
            Names with no CRD: names on the register that name no firm. Attach one to a firm by its
            CRD and it shows as that firm’s other name.
          </p>
        </HelpPopover>
      </h2>
      {error && (
        <LoadError
          message={
            data === null
              ? `Could not read the firms met at registration. ${error}`
              : `Could not refresh — showing them as last read. ${error}`
          }
        />
      )}
      {done && <NoteLine note={done} />}

      {open && (
        <div id={bodyId} className="grid gap-3">
          {seen.length > 0 && (
            <DataTable
              label="Met at registration"
              columns={seenColumns}
              rows={seen}
              rowKey={(firm) => String(firm.finraFirmId)}
              rowProps={(firm) =>
                ({ 'data-finra-firm': firm.finraFirmId }) as React.HTMLAttributes<HTMLTableRowElement>
              }
              minWidth="48rem"
            />
          )}
          {noCrd.length > 0 && (
            <DataTable
              label="Names with no CRD"
              columns={nameColumns}
              rows={noCrd}
              rowKey={(name) => name.code}
              rowProps={(name) => ({ 'data-firm': name.code }) as React.HTMLAttributes<HTMLTableRowElement>}
              minWidth="48rem"
            />
          )}
        </div>
      )}
    </section>
  );
}

/** What a row's writes need from the block. */
interface IRowWrites {
  register: IAdminTeamFirm[];
  notPermitted?: string;
  begin: () => void;
  landed: (text: string) => Promise<void>;
  lost: () => Promise<void>;
  /** The page read again — after a refusal that found the register name gone. */
  reread: () => Promise<boolean>;
}

/** The name a firm met at registration is said by — the lookup's, or its CRD. */
function seenName(seen: IAdminTeamFirmUnmapped): string {
  return seen.firmName ?? `CRD ${seen.finraFirmId}`;
}

function metColumns(
  writes: IRowWrites & { onAddAsFirm: (seen: IAdminTeamFirmUnmapped) => void }
): IDataColumn<IAdminTeamFirmUnmapped>[] {
  return [
    {
      id: 'firm',
      header: 'Met at registration',
      cell: (seen) => (
        <>
          {seen.firmName === null ? (
            <span className="text-muted-foreground">No name from the lookup</span>
          ) : (
            <span className="font-medium break-words">{seen.firmName}</span>
          )}
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">CRD {seen.finraFirmId}</p>
        </>
      )
    },
    { id: 'registrations', header: 'Registrations', numeric: true, cell: (seen) => seen.registrations },
    {
      id: 'last-seen',
      header: 'Last seen',
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (seen) => formatDateTime(seen.lastSeenAt)
    },
    {
      id: 'match',
      header: 'Match',
      cell: (seen) => (
        <div className="grid gap-2">
          <span className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={writes.notPermitted !== undefined}
              aria-describedby={writes.notPermitted}
              onClick={() => writes.onAddAsFirm(seen)}
            >
              Add as a firm
            </Button>
            <AttachToName seen={seen} {...writes} />
          </span>
        </div>
      )
    }
  ];
}

/** One attach press: the write's words said once it landed, a refusal beside the control it came
 *  from, and an answer that never arrived left to the block to say. */
function useAttach({ begin, landed, lost, reread }: IRowWrites) {
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const run = async (write: () => Promise<string>, fallback: string) => {
    begin();
    setBusy(true);
    setRefused(null);
    try {
      await landed(await write());
    } catch (error) {
      if (mayHaveLanded(error)) await lost();
      else {
        setRefused(refusalText(error, fallback));
        if (isGone(error)) void reread();
      }
    } finally {
      setBusy(false);
    }
  };
  return { busy, refused, setRefused, run };
}

/** "Attach to a firm" of a firm met at registration: a register name picked, then confirmed —
 *  saying what else it names — gains the CRD. */
function AttachToName({ seen, ...writes }: IRowWrites & { seen: IAdminTeamFirmUnmapped }) {
  const { register, notPermitted } = writes;
  const [chosen, setChosen] = React.useState<Nullable<{ code: string; label: string }>>(null);
  const { busy, refused, run } = useAttach(writes);
  const crd = seen.finraFirmId;
  const others = register.find((name) => name.code === chosen?.code)?.finraFirmIds ?? [];

  const attach = () => {
    if (chosen === null) return;
    return run(async () => {
      await attachName(chosen, crd);
      return `CRD ${crd} (${seenName(seen)}) is named “${chosen.label}” now.`;
    }, 'Could not attach the CRD to that name.');
  };

  if (chosen === null) {
    return (
      <NamePicker
        label={`Attach ${seenName(seen)} to a firm`}
        trigger="Attach to a firm"
        register={register}
        taken={new Set()}
        allowNew={false}
        disabled={notPermitted !== undefined}
        describedBy={notPermitted}
        onPick={(choice) => {
          if (choice.kind === 'existing') setChosen(choice);
        }}
      />
    );
  }
  return (
    <div className="grid max-w-sm basis-full gap-1.5">
      <p className="text-xs">
        Attach CRD {crd} to “{chosen.label}”?
        {others.length > 0 ? ` It also names CRD ${others.join(', ')}.` : ''}
      </p>
      <RouteChangeLine />
      <span className="flex gap-1.5">
        <Button type="button" size="sm" disabled={busy} onClick={() => void attach()}>
          {busy ? 'Attaching…' : 'Attach'}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setChosen(null)}>
          Cancel
        </Button>
      </span>
      {refused && (
        <p role="alert" className="text-xs text-destructive">
          {refused}
        </p>
      )}
    </div>
  );
}

function noCrdColumns(writes: IRowWrites): IDataColumn<IAdminTeamFirm>[] {
  return [
    {
      id: 'name',
      header: 'Names with no CRD',
      cell: (name) => (
        <>
          <span className="font-medium break-words">{name.label}</span>
          {name.aliases.length > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Also spelled {name.aliases.join(', ')}
            </p>
          )}
        </>
      )
    },
    { id: 'teams', header: 'Teams', numeric: true, cell: (name) => name.teams },
    {
      id: 'last-change',
      header: 'Last change',
      className: 'text-xs text-muted-foreground',
      cell: (name) => changeStamp(name.updatedAt, name.updatedBy)
    },
    { id: 'match', header: 'Match', cell: (name) => <AttachCrd name={name} {...writes} /> }
  ];
}

/** "Attach to a firm" of a name with no CRD: the CRD typed, added to the name's set. */
function AttachCrd({ name, ...writes }: IRowWrites & { name: IAdminTeamFirm }) {
  const { notPermitted } = writes;
  const [crd, setCrd] = React.useState('');
  const { busy, refused, setRefused, run } = useAttach(writes);

  const attach = async () => {
    const id = crdOf(crd);
    if (id === null) return setRefused(CRD_DIGITS);
    await run(async () => {
      await attachName(name, id);
      return `“${name.label}” names CRD ${id} now.`;
    }, 'Could not attach this name.');
  };

  return (
    <form
      className="grid gap-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        void attach();
      }}
    >
      <span className="flex flex-wrap items-center gap-2">
        <Input
          value={crd}
          onChange={(event) => setCrd(event.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="Firm CRD"
          aria-label={`Firm CRD for ${name.label}`}
          disabled={busy || notPermitted !== undefined}
          className="h-8 w-32"
        />
        <Button
          type="submit"
          size="sm"
          variant="outline"
          disabled={busy || crd.trim() === '' || notPermitted !== undefined}
          aria-describedby={notPermitted}
        >
          {busy ? 'Attaching…' : 'Attach to a firm'}
        </Button>
      </span>
      {/* No confirmation here: the sentence is said beside the box once a CRD is typed. */}
      {crd.trim() !== '' && <RouteChangeLine />}
      {refused && (
        <p role="alert" className="text-xs text-destructive">
          {refused}
        </p>
      )}
    </form>
  );
}
